import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend,
} from "recharts";
import { RotateCcw, Sparkles } from "lucide-react";

// Pure client-side calculator — no API calls. Model:
//   • Price appreciates at `priceGrowth`% annually.
//   • Dividend yield starts at `startYield`% of purchase price.
//   • Dividend grows at `divGrowth`% annually (so YoC compounds).
//   • DRIP ON: every dollar of dividend buys more shares at that year's price.
//   • DRIP OFF: dividends are pocketed and NOT reinvested.
// Yearly step (not monthly) — accurate enough for a visualization; keeps
// the useMemo cheap so the sliders feel instant.
function project({ principal, years, startYield, divGrowth, priceGrowth }) {
  const startPrice = 100; // arbitrary; doesn't affect % results
  let sharesOn = principal / startPrice;
  let sharesOff = principal / startPrice;
  let cashDividendsOff = 0;

  const rows = [];
  for (let y = 0; y <= years; y++) {
    const price = startPrice * Math.pow(1 + priceGrowth / 100, y);
    const dpsThisYear = (startYield / 100) * startPrice * Math.pow(1 + divGrowth / 100, y);

    // Position values at start of year y (before this year's dividend is applied).
    const valueOn = sharesOn * price;
    const valueOff = sharesOff * price;

    // Annual dividend cash generated in year y.
    const divOn = sharesOn * dpsThisYear;
    const divOff = sharesOff * dpsThisYear;

    // DRIP ON: reinvest at current price.
    if (y < years) {
      sharesOn += divOn / price;
      cashDividendsOff += divOff;
    }

    rows.push({
      year: y,
      valueOn: Math.round(valueOn),
      valueOff: Math.round(valueOff + cashDividendsOff),  // include pocketed cash on OFF track
      valueOffMarket: Math.round(valueOff),               // just the market value on OFF track
      annualIncomeOn: Math.round(divOn),
      annualIncomeOff: Math.round(divOff),
    });
  }
  return rows;
}

const DEFAULTS = { principal: 10000, years: 20, startYield: 3.5, divGrowth: 10, priceGrowth: 4 };

const fmt$ = (v) => `$${Math.round(v).toLocaleString()}`;

export default function DripCalculator() {
  const [principal, setPrincipal] = useState(DEFAULTS.principal);
  const [years, setYears] = useState(DEFAULTS.years);
  const [startYield, setStartYield] = useState(DEFAULTS.startYield);
  const [divGrowth, setDivGrowth] = useState(DEFAULTS.divGrowth);
  const [priceGrowth, setPriceGrowth] = useState(DEFAULTS.priceGrowth);

  const data = useMemo(
    () => project({ principal, years, startYield, divGrowth, priceGrowth }),
    [principal, years, startYield, divGrowth, priceGrowth]
  );
  const last = data[data.length - 1];
  const delta = last.valueOn - last.valueOff;
  const uplift = last.valueOff > 0 ? (delta / last.valueOff) * 100 : 0;

  const reset = () => {
    setPrincipal(DEFAULTS.principal);
    setYears(DEFAULTS.years);
    setStartYield(DEFAULTS.startYield);
    setDivGrowth(DEFAULTS.divGrowth);
    setPriceGrowth(DEFAULTS.priceGrowth);
  };

  return (
    <Card className="border-primary/25 bg-primary/[0.02]" data-testid="drip-calculator">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Interactive · Move the sliders
            </div>
            <h4 className="serif text-2xl mt-1">See DRIP work on your numbers.</h4>
          </div>
          <button
            onClick={reset}
            className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="drip-reset"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr,2fr] gap-6 items-start">
          {/* Sliders */}
          <div className="space-y-4">
            <SliderRow
              label="Initial investment"
              value={principal}
              min={1000}
              max={500000}
              step={1000}
              onChange={setPrincipal}
              display={fmt$(principal)}
              testId="drip-principal"
            />
            <SliderRow
              label="Time horizon"
              value={years}
              min={5}
              max={40}
              step={1}
              onChange={setYears}
              display={`${years} years`}
              testId="drip-years"
            />
            <SliderRow
              label="Starting dividend yield"
              value={startYield}
              min={1}
              max={10}
              step={0.1}
              onChange={setStartYield}
              display={`${startYield.toFixed(1)}%`}
              testId="drip-yield"
            />
            <SliderRow
              label="Dividend growth per year"
              value={divGrowth}
              min={0}
              max={20}
              step={0.5}
              onChange={setDivGrowth}
              display={`${divGrowth.toFixed(1)}%`}
              hint="SCHD ≈ 12% · Aristocrats 6–8% · JEPI ~2%"
              testId="drip-divgrowth"
            />
            <SliderRow
              label="Price appreciation per year"
              value={priceGrowth}
              min={0}
              max={15}
              step={0.5}
              onChange={setPriceGrowth}
              display={`${priceGrowth.toFixed(1)}%`}
              hint="S&P 500 long-run ≈ 7%"
              testId="drip-pricegrowth"
            />
          </div>

          {/* Chart + results */}
          <div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <ResultCard label="DRIP ON" value={fmt$(last.valueOn)} tone="text-primary" testId="drip-result-on" />
              <ResultCard label="DRIP OFF" value={fmt$(last.valueOff)} tone="text-muted-foreground" testId="drip-result-off" />
              <ResultCard
                label="DRIP uplift"
                value={`+${fmt$(delta)}`}
                sub={`+${uplift.toFixed(0)}%`}
                tone="text-secondary"
                testId="drip-result-delta"
              />
            </div>

            <div className="h-64 rounded-md border border-border bg-card p-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dripOn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F3D32" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0F3D32" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="dripOff" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B7355" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#8B7355" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="year" tick={{ fontSize: 10 }} tickFormatter={(v) => `y${v}`} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={55}
                    tickFormatter={(v) => v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{ background: "#F7F5F0", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 6, fontSize: 12 }}
                    formatter={(v, name) => [fmt$(v), name === "valueOn" ? "DRIP ON" : "DRIP OFF"]}
                    labelFormatter={(l) => `Year ${l}`}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                    formatter={(v) => v === "valueOn" ? "DRIP ON (compounds)" : "DRIP OFF (spent)"}
                  />
                  <Area type="monotone" dataKey="valueOff" stroke="#8B7355" strokeWidth={2} fill="url(#dripOff)" />
                  <Area type="monotone" dataKey="valueOn" stroke="#0F3D32" strokeWidth={2.5} fill="url(#dripOn)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <p className="text-[11px] text-muted-foreground italic mt-2 leading-relaxed">
              Model assumes constant yield growth + price appreciation. Real markets zig-zag — DPP&apos;s
              historic backtests use real daily data. Use this to <em>feel</em> the compounding, not to predict the future.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SliderRow({ label, value, min, max, step, onChange, display, hint, testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <Label className="text-xs">{label}</Label>
        <span className="mono text-sm text-primary font-semibold" data-testid={`${testId}-value`}>{display}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testId}
      />
      {hint && <div className="mono text-[9px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function ResultCard({ label, value, sub, tone, testId }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2.5" data-testid={testId}>
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`serif text-xl leading-tight mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="mono text-[10px] mt-0.5 text-secondary">{sub}</div>}
    </div>
  );
}
